import { useState, useEffect, useCallback, useRef } from 'react';

interface UsePreviewProcessorProps {
  previewHtml: string;
  getPreviewHtmlWithExampleValues: (html: string) => string;
  isChangeDetected?: boolean;
}

export function usePreviewProcessor({ 
  previewHtml, 
  getPreviewHtmlWithExampleValues,
  isChangeDetected = false
}: UsePreviewProcessorProps) {
  const [renderedHtml, setRenderedHtml] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // Debounce timer reference
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Track if we have at least rendered once
  const hasRenderedRef = useRef<boolean>(false);
  // Keep track of the latest HTML for comparisons
  const latestHtmlRef = useRef<string>('');
  
  // Process HTML with template variables
  const processHtml = useCallback((html: string) => {
    if (!html) return '';
    try {
      console.log('Processing preview HTML, length:', html.length);
      
      // Apply template variables
      const processed = getPreviewHtmlWithExampleValues(html);
      console.log('Processed HTML with variables, length:', processed.length);
      
      return processed;
    } catch (error) {
      console.error('Error processing HTML preview:', error);
      return `
        <div style="color: red; padding: 20px; font-family: sans-serif;">
          <h3>Error Processing Template</h3>
          <p>${error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      `;
    }
  }, [getPreviewHtmlWithExampleValues]);
  
  // Update preview with debouncing
  useEffect(() => {
    // Skip empty HTML
    if (!previewHtml) return;
    
    // Skip if HTML hasn't changed
    if (previewHtml === latestHtmlRef.current && hasRenderedRef.current) {
      console.log('HTML has not changed, skipping preview update');
      return;
    }
    
    // Update latest HTML reference
    latestHtmlRef.current = previewHtml;
    
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set loading state unless we've already rendered once
    if (!hasRenderedRef.current) {
      setIsLoading(true);
    }
    
    // Debounce the update to prevent excessive rendering
    debounceTimerRef.current = setTimeout(() => {
      console.log('Debounced preview update triggered');
      const processedHtml = processHtml(previewHtml);
      setRenderedHtml(processedHtml);
      hasRenderedRef.current = true;
      
      // Add a small delay before removing loading state for smoother transitions
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }, isChangeDetected ? 150 : 100); // Faster preview updates
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [previewHtml, processHtml, isChangeDetected]);
  
  // Handle iframe load event
  const handleIframeLoad = useCallback(() => {
    console.log('Iframe load event handled');
    setIsLoading(false);
  }, []);
  
  return {
    renderedHtml,
    isLoading,
    handleIframeLoad,
  };
}
